import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

import { useAuthStore } from '@/stores/auth-store';
import { SyncStatusDrawer } from '@/components/sync/SyncStatusDrawer';

interface MoreRowProps {
  label: string;
  to?: string;
  onClick?: () => void;
  locked?: boolean;
  'data-testid'?: string;
}

function MoreRow({ label, to, onClick, locked, 'data-testid': testId }: MoreRowProps) {
  const baseClass =
    'w-full py-3.5 px-4 border-b-[0.5px] border-bark-600 flex items-center justify-between text-left transition-colors';

  const inner = (
    <>
      <span className={`text-body-sm ${locked ? 'text-[var(--color-text-placeholder)]' : 'text-[var(--color-text-primary)]'}`}>
        {label}
      </span>
      {/* Locked-row pattern available if future rows are locked: aria-disabled="true" + <Lock> icon */}
      <ChevronRight className="text-bark-400" size={16} aria-hidden="true" />
    </>
  );

  if (locked) {
    return (
      <button type="button" className={`${baseClass} cursor-default`} data-testid={testId} aria-disabled="true" disabled>
        {inner}
      </button>
    );
  }

  if (to) {
    return (
      <Link to={to} className={`${baseClass} hover:bg-[var(--color-elevated)]`} data-testid={testId}>
        {inner}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${baseClass} hover:bg-[var(--color-elevated)]`}
      data-testid={testId}
    >
      {inner}
    </button>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <p
      data-testid="section-header"
      className="text-label uppercase tracking-widest text-[var(--color-text-muted)] px-4 pt-5 pb-1"
    >
      {title}
    </p>
  );
}

export function MorePage() {
  const { signOut } = useAuthStore();
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  async function handleLogout() {
    setIsLoggingOut(true);
    try {
      await signOut();
      navigate('/auth');
    } catch {
      setIsLoggingOut(false);
      setShowLogoutConfirm(false);
    }
  }

  return (
    <div className="flex flex-col pb-6 bg-[var(--color-bg)]" data-testid="more-page">
      <h1 className="font-display text-display-lg text-[var(--color-text-display)] px-4 pt-6 pb-2">More</h1>

      {/* YOUR LAND */}
      <SectionHeader title="Your Land" />
      <MoreRow label="Properties & Locations" to="/land/properties" data-testid="row-properties" />
      <MoreRow label="Seasons" to="/seasons" data-testid="row-seasons" />

      {/* HUNT JOURNAL */}
      <SectionHeader title="Hunt Journal" />
      <MoreRow label="Hunt Trips" to="/hunt" data-testid="row-hunt-trips" />
      <MoreRow label="Harvest Records" to="/more/harvests" data-testid="row-harvest-records" />

      {/* ACCOUNT */}
      <SectionHeader title="Account" />
      <MoreRow label="Profile & Preferences" to="/profile" data-testid="row-profile" />
      <MoreRow label="Subscription" to="/subscription" data-testid="row-subscription" />
      <MoreRow label="Sync" onClick={() => setDrawerOpen(true)} data-testid="row-sync" />
      <MoreRow label="Notifications" to="/profile" data-testid="row-notifications" />
      <MoreRow
        label="Log Out"
        onClick={() => setShowLogoutConfirm(true)}
        data-testid="row-logout"
      />

      {/* SUPPORT */}
      <SectionHeader title="Support" />
      <MoreRow label="About Ridgen" to="/about" data-testid="row-about" />
      <MoreRow
        label="Send Feedback"
        onClick={() => {
          window.location.href = 'mailto:feedback@ridgen.app';
        }}
        data-testid="row-feedback"
      />

      {/* LEGAL */}
      <SectionHeader title="Legal" />
      <MoreRow label="Privacy Policy" to="/privacy" data-testid="row-privacy" />
      <MoreRow label="Terms of Service" to="/terms" data-testid="row-terms" />

      {/* Sync Status Drawer */}
      <SyncStatusDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />

      {/* Logout confirm dialog */}
      {showLogoutConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60"
          data-testid="logout-confirm-dialog"
        >
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl shadow-card-lg w-full max-w-sm p-6 flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <h2 className="font-display text-display-sm text-[var(--color-text-display)]">Log Out?</h2>
              <p className="text-body-sm text-[var(--color-text-muted)]">
                You'll need to sign in again to access your data.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => void handleLogout()}
                disabled={isLoggingOut}
                className="flex-1 py-2.5 px-4 rounded-lg bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] text-white text-body-sm font-medium transition-colors disabled:opacity-50"
                data-testid="logout-confirm-btn"
              >
                {isLoggingOut ? 'Logging out…' : 'Log Out'}
              </button>
              <button
                type="button"
                onClick={() => setShowLogoutConfirm(false)}
                disabled={isLoggingOut}
                className="py-2.5 px-4 rounded-lg border border-[var(--color-border)] text-[var(--color-text-muted)] text-body-sm font-medium transition-colors hover:bg-[var(--color-elevated)] disabled:opacity-50"
                data-testid="logout-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
